import { CLASSIC_SHOP_URL, IDLE_HINT_DELAY_MS } from './constants';
import { track } from '../analytics/analytics';
import { getProductRepository } from '../commerce/product.repository';
import { createProductPanel } from '../components/product-card/product-panel';
import { createProductHover } from '../components/product-badges/product-hover';
import { createProductStage } from '../components/product-stage/product-stage';
import { emit, on } from './events';
import { loadCutouts } from '../commerce/cutouts';
import { loadOffers } from '../commerce/offers';
import { registerServiceWorker } from './pwa';
import { currentScene, navigate, startRouter } from './router';
import {
  getState,
  hasVisited,
  loadState,
  markInteractionDiscovered,
  markSceneVisited,
  resetState,
} from './state';
import { getCartService } from '../commerce/cart.service';
import { createCartPanel } from '../components/cart-panel/cart-panel';
import { createNavigation } from '../components/navigation/navigation';
import { createSimplifiedNav } from '../components/simplified-nav/simplified-nav';
import { createToast } from '../components/toast/toast';
import { createDialogueBox, getDialogue } from '../interactions/dialogue';
import { noteActivation, resetEggProgress } from '../interactions/easter-egg';
import { revealHotspots, setHotspotState } from '../interactions/hotspot';
import { createConseiller } from '../components/conseiller/conseiller';
import { createInspector } from '../interactions/inspect';
import { playChain, stealHotspot } from '../interactions/product-interaction';
import { claimReward, pickUpBone, QUEST_REWARD } from '../quest/quest.service';
import { createQuestIndicator } from '../quest/quest.ui';
import { getInspection, getScene, registerAllContent } from '../scenes/scene.registry';
import { renderScene, type RenderedScene } from '../scenes/scene.renderer';
import { playTransition, prefersReducedMotion, transitionFor } from '../scenes/scene.transitions';
import type { SceneHotspot, SceneId } from '../scenes/scene.types';

/** Le chien rend le produit volé au bout de ce délai. */
const THEFT_DURATION_MS = 9000;

export function mountApp(root: HTMLElement): void {
  loadState();
  // Le manifeste des photos détourées arrive vite : les scènes s'en servent dès qu'il est là.
  void loadCutouts();
  // Les offres de lot sont un petit fichier : on l'a avant la première fiche.
  void loadOffers();
  registerAllContent();

  root.replaceChildren();
  root.className = 'app';

  const header = document.createElement('header');
  header.className = 'app__header';

  const title = document.createElement('p');
  title.className = 'app__title';
  title.textContent = 'La Boutique Chien Heureux';

  const controls = document.createElement('div');
  controls.className = 'app__controls';

  const revealButton = document.createElement('button');
  revealButton.type = 'button';
  revealButton.className = 'control';
  revealButton.textContent = 'Montrer les éléments interactifs';

  const simplifiedButton = document.createElement('button');
  simplifiedButton.type = 'button';
  simplifiedButton.className = 'control';
  simplifiedButton.setAttribute('aria-pressed', 'false');
  simplifiedButton.textContent = 'Navigation simplifiée';
  // Repère pour le panneau : un clic sur cette commande ne doit pas être lu
  // comme un clic à l'extérieur, sinon la fermeture annule l'ouverture.
  simplifiedButton.dataset['role'] = 'simplified-toggle';

  const restartButton = document.createElement('button');
  restartButton.type = 'button';
  restartButton.className = 'control control--quiet';
  restartButton.textContent = 'Recommencer la visite';

  /*
   * Sur téléphone, les trois commandes passaient à la ligne et le bandeau
   * mangeait deux fois sa hauteur, au détriment du décor. Elles sont donc
   * repliées derrière un bouton, et le bandeau tient sur une seule ligne.
   * Sur grand écran le tiroir reste ouvert et le bouton disparaît, l'affichage
   * ne change pas.
   */
  const controlsToggle = document.createElement('button');
  controlsToggle.type = 'button';
  controlsToggle.className = 'control app__controls-toggle';
  controlsToggle.setAttribute('aria-expanded', 'false');
  controlsToggle.setAttribute('aria-controls', 'app-controls');
  controlsToggle.setAttribute('aria-label', 'Afficher les options de visite');
  controlsToggle.textContent = 'Options';

  controls.id = 'app-controls';
  controls.append(revealButton, simplifiedButton, restartButton);

  controlsToggle.addEventListener('click', () => {
    const ouvert = controlsToggle.getAttribute('aria-expanded') !== 'true';
    controlsToggle.setAttribute('aria-expanded', String(ouvert));
    controlsToggle.setAttribute(
      'aria-label',
      ouvert ? 'Masquer les options de visite' : 'Afficher les options de visite',
    );
    header.classList.toggle('is-open', ouvert);
  });

  // Un choix fait, le tiroir se referme : il recouvre le haut du décor.
  for (const commande of [revealButton, simplifiedButton, restartButton]) {
    commande.addEventListener('click', () => {
      if (controlsToggle.getAttribute('aria-expanded') !== 'true') return;
      controlsToggle.setAttribute('aria-expanded', 'false');
      header.classList.remove('is-open');
    });
  }

  header.append(title, controlsToggle, controls);

  const main = document.createElement('main');
  main.className = 'app__main';
  main.id = 'scene-main';
  main.tabIndex = -1;

  const sceneHost = document.createElement('div');
  sceneHost.className = 'app__scene-host';

  const quest = createQuestIndicator();

  /** Ouvre ou referme la navigation simplifiée, d'où que vienne la demande. */
  function basculerNavigationSimplifiee(ouverte: boolean): void {
    if ((simplifiedButton.getAttribute('aria-pressed') === 'true') === ouverte) return;
    simplifiedButton.setAttribute('aria-pressed', String(ouverte));
    simplified.setEnabled(ouverte);
    if (ouverte) track('simplified_navigation_enabled', { sceneId: currentScene() });
  }

  const simplified = createSimplifiedNav(
    (hotspot) => handleHotspot(hotspot),
    () => basculerNavigationSimplifiee(false),
  );
  const navigation = createNavigation(
    (sceneId) => navigate(sceneId),
    () => void openCart(),
  );

  main.append(sceneHost, simplified.element);
  root.append(header, main, quest.element, navigation.element);

  const dialogueBox = createDialogueBox(root);
  const toast = createToast(root);
  const productStage = createProductStage(root);
  const productHover = createProductHover(root);
  const productPanel = createProductPanel(root, {
    onOpenCart: () => void openCart(),
    onClose: () => productStage.close(),
  });
  const cartPanel = createCartPanel(root);
  const inspector = createInspector(root);
  const conseiller = createConseiller(root);

  let rendered: RenderedScene | null = null;
  let previous: SceneId | null = null;
  let idleTimer: number | undefined;

  function scheduleIdleHint(): void {
    if (idleTimer !== undefined) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      if (rendered) revealHotspots(rendered.element, 1400);
    }, IDLE_HINT_DELAY_MS);
  }

  function speak(dialogueId: string): void {
    const dialogue = getDialogue(dialogueId);
    if (dialogue) dialogueBox.show(dialogue);
  }

  function markDiscovered(hotspotId: string): void {
    markInteractionDiscovered(hotspotId);
    const element = rendered?.hotspotElements.get(hotspotId);
    if (element) setHotspotState(element, 'discovered');
  }

  /** Rejoue une animation même si elle vient d'être jouée. */
  function animate(hotspotId: string, animation: string): void {
    const element = rendered?.hotspotElements.get(hotspotId);
    if (!element) return;
    element.classList.remove(animation);
    // Forcer le reflow pour pouvoir rejouer l'animation immédiatement.
    void element.offsetWidth;
    element.classList.add(animation);
    window.setTimeout(() => element.classList.remove(animation), 900);
  }

  /** Un chien emporte un produit du rayon, puis le rapporte. */
  function stealProduct(hotspotId: string): void {
    const theft = stealHotspot(rendered?.hotspotElements.get(hotspotId), THEFT_DURATION_MS, () =>
      toast.show('Le produit est revenu à sa place. Légèrement humide.'),
    );
    if (theft) {
      toast.show('Un chien vient de partir avec un produit. Il le rapportera. Probablement.');
    }
  }

  /** Réactions spécifiques à certains objets, en plus de l'animation de base. */
  function playSceneEffect(hotspotId: string): void {
    switch (hotspotId) {
      case 'alimentation.dog':
        stealProduct('alimentation.product-2');
        return;
      case 'alimentation.falling':
        animate('alimentation.falling', 'is-falling');
        return;
      case 'friandises.bag':
        animate('friandises.bag', 'is-swinging');
        return;
      case 'friandises.mastication':
        // Réaction en chaîne : la mastication tombe, le sachet oscille, le bocal tremble.
        animate('friandises.mastication', 'is-falling');
        if (rendered) {
          playChain(rendered.hotspotElements, [
            { hotspotId: 'friandises.bag', animation: 'is-swinging', delayMs: 260 },
            { hotspotId: 'friandises.jar', animation: 'is-shaking', delayMs: 240 },
          ]);
        }
        return;
      case 'comptoir.cart':
        void openCart();
        return;
      case 'comptoir.vendeur':
        // Le conseiller du comptoir, c'est le chatbot du site.
        productPanel.close();
        cartPanel.close();
        conseiller.open(rendered?.hotspotElements.get('comptoir.vendeur'));
        track('hotspot_clicked', { sceneId: 'comptoir', hotspotId: 'conseiller' });
        return;
      default:
        animate(hotspotId, 'is-shaking');
    }
  }

  /** Le panier s'affiche dans la boutique ; le checkout part du panneau, pas d'ici. */
  async function openCart(): Promise<void> {
    // La fiche produit et le panier occupent la même place à l'écran.
    productPanel.close();
    await cartPanel.open();
  }

  /** Ouvre la fiche produit ; un handle introuvable donne un message clair, pas un écran vide. */
  async function openProduct(handle: string, origine?: HTMLElement): Promise<void> {
    // Les deux panneaux occupent la même place : un seul reste ouvert.
    cartPanel.close();
    try {
      const product = await getProductRepository().getProductByHandle(handle);
      if (!product) {
        productPanel.openError(
          'La boutique virtuelle a perdu la trace de ce produit. Vous pouvez le chercher dans la boutique classique.',
        );
        return;
      }
      productHover.hide();
      /*
       * Le produit sort de l'étagère à gauche pendant que la fiche s'ouvre à
       * droite. Ce partage de l'écran n'existe que sur grand écran : en feuille
       * du bas, la fiche occupe déjà la largeur et porte son propre carrousel,
       * si bien que la photo détourée restait derrière elle sans rien montrer.
       */
      if (window.innerWidth >= 768) productStage.open(product, origine);
      productPanel.open(product);
    } catch {
      productPanel.openError(
        'Le catalogue ne répond pas pour le moment. La boutique classique reste ouverte.',
      );
    }
  }

  function handleHotspot(hotspot: SceneHotspot, origine?: HTMLElement): void {
    scheduleIdleHint();
    emit('hotspot:activated', { sceneId: currentScene(), hotspotId: hotspot.id });
    track('hotspot_clicked', { sceneId: currentScene(), hotspotId: hotspot.id });
    markDiscovered(hotspot.id);

    const egg = noteActivation(hotspot.id);
    if (egg) toast.show(egg.message);

    switch (hotspot.action) {
      case 'navigate':
        if (hotspot.target) void showScene(hotspot.target as SceneId);
        return;

      case 'external-link':
        if (hotspot.target) {
          track('classic_shop_opened', { sceneId: currentScene(), source: hotspot.id });
          window.open(hotspot.target, '_blank', 'noopener');
        }
        return;

      case 'product':
        if (hotspot.target) void openProduct(hotspot.target, origine);
        return;

      case 'collectible': {
        if (!hotspot.target) return;
        const result = pickUpBone(hotspot.target);
        quest.refresh();
        if (result.collected) {
          track('collectible_found', { sceneId: currentScene(), hotspotId: hotspot.id });
        }
        toast.show(
          result.collected
            ? `Un os. Le troisième tiroir de l'enquête s'ouvre. (${result.total} / 3)`
            : 'Cet os est déjà dans votre poche. Il n’a pas bougé.',
        );
        return;
      }

      case 'animation': {
        playSceneEffect(hotspot.id);
        // Le panier et le conseiller ouvrent un panneau : pas de réplique en plus.
        const muets = ['comptoir.cart', 'comptoir.vendeur'];
        if (hotspot.target && !muets.includes(hotspot.id)) speak(hotspot.target);
        return;
      }

      case 'inspect': {
        const content = hotspot.target ? getInspection(hotspot.target) : undefined;
        if (content) {
          inspector.open(content);
        } else if (hotspot.target) {
          speak(hotspot.target);
        }
        return;
      }

      case 'dialogue':
      default:
        if (hotspot.id === 'comptoir.reward') {
          const { granted, alreadyClaimed } = claimReward();
          if (granted) {
            speak('comptoir.reward');
            toast.show(`${QUEST_REWARD.title} — ${QUEST_REWARD.message}`);
          } else if (alreadyClaimed) {
            toast.show('L’enquête est déjà close. Le suspect dort toujours.');
          } else {
            toast.show('Il manque encore des os. Le vendeur reste poli, mais ferme.');
          }
          return;
        }
        if (hotspot.target) speak(hotspot.target);
    }
  }

  async function showScene(sceneId: SceneId): Promise<void> {
    if (currentScene() !== sceneId) {
      navigate(sceneId);
      return;
    }
    await paint(sceneId);
  }

  async function paint(sceneId: SceneId): Promise<void> {
    const definition = getScene(sceneId);
    const transition = transitionFor(previous, sceneId);

    if (rendered) {
      emit('scene:left', { sceneId: previous ?? sceneId });
      await playTransition(rendered.element, transition, 'out');
      rendered.destroy();
    }

    // Chaque scène repart d'une progression d'easter egg vierge.
    resetEggProgress();
    rendered = renderScene(definition, {
      onHotspot: handleHotspot,
      onProductHover: (product, anchor) => productHover.show(product, anchor),
      onProductLeave: () => productHover.hide(),
    });
    sceneHost.append(rendered.element);
    void playTransition(rendered.element, transition, 'in');

    navigation.setActive(sceneId);
    simplified.update(definition);
    document.title = `${definition.title} — La Boutique Chien Heureux`;

    const firstVisit = !hasVisited(sceneId);
    markSceneVisited(sceneId);
    if (firstVisit && definition.entryDialogueId) speak(definition.entryDialogueId);

    previous = sceneId;
    emit('scene:entered', { sceneId });
    track('scene_viewed', { sceneId });
    scheduleIdleHint();
  }

  revealButton.addEventListener('click', () => {
    if (rendered) revealHotspots(rendered.element);
  });

  simplifiedButton.addEventListener('click', () => {
    basculerNavigationSimplifiee(simplifiedButton.getAttribute('aria-pressed') !== 'true');
  });

  restartButton.addEventListener('click', () => {
    resetState();
    resetEggProgress();
    quest.refresh();
    previous = null;
    void paint(currentScene());
    toast.show('Visite remise à zéro. La boutique fait semblant de ne rien savoir.');
  });

  document.addEventListener('pointerdown', scheduleIdleHint);
  document.addEventListener('keydown', scheduleIdleHint);

  navigation.setCartCount(getCartService().lineCount());
  on('cart:changed', ({ count }) => navigation.setCartCount(count));

  on('quest:completed', () => track('quest_completed', { sceneId: currentScene() }));
  on('easter-egg:found', ({ eggId }) => track('easter_egg_found', { source: eggId }));
  on('dialogue:shown', ({ dialogueId }) => track('dialogue_viewed', { source: dialogueId }));

  startRouter((sceneId) => {
    void paint(sceneId);
  });

  track('virtual_shop_started', { sceneId: currentScene() });
  if (prefersReducedMotion()) track('reduced_motion_enabled', {});

  registerServiceWorker((apply) => {
    toast.show('Une nouvelle version de la boutique est prête. Touchez pour la charger.', 8000);
    toast.element.addEventListener('click', apply, { once: true });
  });

  // Sortie de secours permanente : le site classique reste toujours atteignable.
  if (!getState().visitedScenes.length) {
    console.info(`Boutique classique : ${CLASSIC_SHOP_URL}`);
  }
}
